import type {
  DOMConversionMap,
  DOMExportOutput,
  EditorConfig,
  LexicalNode,
  SerializedLexicalNode,
  Spread,
} from 'lexical';

import { DecoratorNode } from 'lexical';
import { useTranslation } from 'react-i18next';
import type { JSX } from 'react';

export type SerializedPageBreakNode = Spread<
  {
    type: 'page-break';
    version: 1;
  },
  SerializedLexicalNode
>;

// eslint-disable-next-line react-refresh/only-export-components
function PageBreakComponent(): JSX.Element {
  const { t } = useTranslation('common');

  return (
    <div
      className="my-6 flex items-center justify-center"
      contentEditable={false}
    >
      <div className="relative w-full">
        <div className="border-t border-dashed border-border" />
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-2 text-xs text-muted-foreground/70">
          {t('editor.pageBreak')}
        </span>
      </div>
    </div>
  );
}

export class PageBreakNode extends DecoratorNode<JSX.Element> {
  static getType(): string {
    return 'page-break';
  }

  static clone(node: PageBreakNode): PageBreakNode {
    return new PageBreakNode(node.__key);
  }

  static importJSON(_serializedNode: SerializedPageBreakNode): PageBreakNode {
    return $createPageBreakNode();
  }

  static importDOM(): DOMConversionMap | null {
    return {
      div: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-lexical-page-break')) {
          return null;
        }
        return {
          conversion: convertPageBreakElement,
          priority: 1,
        };
      },
    };
  }

  exportJSON(): SerializedPageBreakNode {
    return {
      type: 'page-break',
      version: 1,
    };
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('div');
    element.setAttribute('data-lexical-page-break', 'true');
    return { element };
  }

  createDOM(config: EditorConfig): HTMLElement {
    const div = document.createElement('div');
    if (config.theme.pageBreak) {
      div.className = config.theme.pageBreak;
    }
    return div;
  }

  updateDOM(): false {
    return false;
  }

  decorate(): JSX.Element {
    return <PageBreakComponent />;
  }

  isInline(): false {
    return false;
  }

  isIsolated(): true {
    return true;
  }

  isKeyboardSelectable(): false {
    return false;
  }
}

function convertPageBreakElement(): { node: PageBreakNode } {
  return { node: $createPageBreakNode() };
}

export function $createPageBreakNode(): PageBreakNode {
  return new PageBreakNode();
}

export function $isPageBreakNode(
  node: LexicalNode | null | undefined
): node is PageBreakNode {
  return node instanceof PageBreakNode;
}
